import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import axios from '../utils/axios';
import useAuth from '../hooks/useAuth';
import { FeatureFlag, FeatureFlagContextType } from '../@types/featureFlag';
import { FeatureKey } from '../constants/features';

// ----------------------------------------------------------------------

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

// ----------------------------------------------------------------------

type FeatureFlagProviderProps = {
  children: ReactNode;
};

function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const { isAuthenticated } = useAuth();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const response = await axios.get('/app/shared/company/feature-flags');
      setFeatureFlags(response.data.data);
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFeatureFlags();
    } else {
      setFeatureFlags([]);
      setIsLoaded(false);
    }
  }, [isAuthenticated, fetchFeatureFlags]);

  const isFeatureEnabled = useCallback(
    (feature: FeatureKey): boolean => {
      const flag = featureFlags.find((f) => f.feature === feature);
      return flag ? flag.is_enabled : true;
    },
    [featureFlags]
  );

  return (
    <FeatureFlagContext.Provider value={{ featureFlags, isLoaded, isFeatureEnabled, refetch: fetchFeatureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export { FeatureFlagContext, FeatureFlagProvider };
